import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, UserRound, LockKeyhole, Eye, EyeOff, ArrowRight, ShieldCheck, LoaderCircle, X, CheckCircle2, AlertCircle } from "lucide-react";
import { loginAdmin } from "../../api/authApi";
import "./Login.css";

function BrandLogo() {
  return (
    <div className="sk-logo">
      <div className="sk-logo-symbol">
        <Heart className="sk-logo-heart-outer" size={54} strokeWidth={1.8} />
        <Heart className="sk-logo-heart-inner" size={23} fill="currentColor" />
      </div>

      <div className="sk-logo-copy">
        <div className="sk-logo-tamil">
          <span>சேலம்</span> <strong>கொங்கு</strong>
        </div>
        <div className="sk-logo-caption">
          <i />திருமண தகவல் மையம்<i />
        </div>
      </div>
    </div>
  );
}

function AlertPopup({ type = "error", title, message, onClose }) {
  const isSuccess = type === "success";
  useEffect(() => {
    const timer = setTimeout(onClose, isSuccess ? 1200 : 3500);
    return () => clearTimeout(timer);
  }, [onClose, isSuccess]);

  return (
    <div className="sk-alert-overlay" role="presentation" onClick={onClose}>
      <div className={`sk-alert-modal ${isSuccess ? "sk-alert-success" : "sk-alert-error"}`}
        role="alertdialog" aria-modal="true" aria-labelledby="sk-alert-title" aria-describedby="sk-alert-message" onClick={(event) => event.stopPropagation()}>
        <button type="button" className="sk-alert-close" onClick={onClose} aria-label="Close alert">
          <X size={18} />
        </button>
        <div className="sk-alert-icon">
          {isSuccess ? (
            <CheckCircle2 size={34} />
          ) : (
            <AlertCircle size={34} />
          )}
        </div>
        <h3 id="sk-alert-title">{title}</h3>
        <p id="sk-alert-message">{message}</p>
        {!isSuccess && (
          <button type="button" className="sk-alert-button" onClick={onClose}>Try Again</button>
        )}
      </div>
    </div>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState(null);
  const closePopup = React.useCallback(() => { setPopup(null); }, []);
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const response = await loginAdmin(username.trim(), password);
      if (!response?.success || !response?.data?.token) {
        throw new Error(
          response?.message || "Invalid login credentials."
        );
      }
      const { token, admin } = response.data;
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(admin || {}));
      setPopup({
        type: "success",
        title: "Login Successful!",
        message: `Welcome back${admin?.full_name ? `, ${admin.full_name}` : ""
          }. Redirecting to dashboard...`,
      });
      setTimeout(() => {
        navigate("/dashboard", { replace: true });
      }, 1000);
    } catch (error) {
      setPopup({
        type: "error",
        title: "Login Failed",
        message:
          error.response?.data?.message ||
          error.message ||
          "Unable to login. Please check your credentials and try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="sk-login-page">
      <section className="sk-login-brand-panel">
        <div className="sk-login-brand-top">
          <BrandLogo />
        </div>
        <div className="sk-login-brand-content">
          <p className="sk-login-eyebrow">
            <span />KONGU VELLALAR MATRIMONY</p>
          <h1>Bringing hearts<br /><span>together.</span></h1>
          <p className="sk-login-description">Manage members, matrimonial profiles, branches, <br className="sk-desktop-break" /> memberships and meaningful matches from one powerful platform.</p>
          <div className="sk-login-benefits">
            <span><ShieldCheck size={16} />Trusted profile management</span>
            <span><Heart size={16} />Meaningful connections</span>
          </div>
        </div>
        <p className="sk-login-copyright">© {new Date().getFullYear()} Salem Kongu Matrimony.All rights reserved.</p>
        <div className="sk-login-decoration sk-login-decoration-one" />
        <div className="sk-login-decoration sk-login-decoration-two" />
      </section>
      <section className="sk-login-form-panel">
        <div className="sk-login-form-wrap">
          <div className="sk-login-mobile-logo">
            <BrandLogo />
          </div>
          <div className="sk-login-heading">
            <span className="sk-login-form-kicker">ADMINISTRATION PORTAL</span>
            <h2>Welcome Back</h2>
            <p>Sign in to your admin account</p>
          </div>
          <form onSubmit={handleSubmit}>
            <label htmlFor="admin-username">Username</label>
            <div className="sk-login-input-wrap">
              <UserRound size={17} />
              <input
                id="admin-username"
                name="username"
                type="text"
                autoComplete="username"
                placeholder="Enter username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                disabled={loading}
                required />
            </div>
            <label htmlFor="admin-password" className="sk-login-password-label">Password</label>
            <div className="sk-login-input-wrap">
              <LockKeyhole size={17} />
              <input
                id="admin-password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                placeholder="Enter password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={loading}
                required />
              <button
                type="button"
                className="sk-login-show-password"
                aria-label={showPassword ? "Hide password" : "Show password"}
                onClick={() => setShowPassword((current) => !current)}
                disabled={loading}>
                {showPassword ? (
                  <EyeOff size={18} />
                ) : (
                  <Eye size={18} />
                )}
              </button>
            </div>
            <button className="sk-login-submit" type="submit" disabled={loading}>
              {loading ? (
                <>
                  Signing In...
                  <LoaderCircle size={18} className="animate-spin" />
                </>
              ) : (
                <>
                  Sign In
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>
          <p className="sk-login-portal-caption">Salem Kongu Matrimony Admin Portal</p>
          <p className="sk-login-security-note"><ShieldCheck size={14} />Authorized administrators only</p>
        </div>
      </section>

      {popup && (
        <AlertPopup type={popup.type} title={popup.title} message={popup.message} onClose={closePopup} />
      )}
    </main>
  );
}